import { createContext, useState, ReactNode, useEffect } from "react";
import { getUserInfo, login } from "../api/authApi";
import { useContext } from "react";
import { jwtDecode } from "jwt-decode";
import { AuthContextType, GroupOption } from "../utils/Type";

export const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [user, setUser] = useState<any>(null);
    const [userId, setUserId] = useState<any>(null);
    const [userName, setUserName] = useState<string | null>(null);
    const [role, setRole] = useState<string | null>(localStorage.getItem("role"));
    const [groupes, setGroupes] = useState<number[] | null>(() => {
        const storedGroupes = localStorage.getItem("groupes");
        return storedGroupes ? JSON.parse(storedGroupes) : null;
    });
    
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchUserInfo = async () => {
            const token = localStorage.getItem("token");
            if (token) {
                try {
                    const decodedToken: any = jwtDecode(token);
                    const userEmail = decodedToken.sub;
                    const expiration = decodedToken.exp * 1000;
    
                    if (Date.now() >= expiration) {
                        signOut();
                        setLoading(false);
                        return;
                    }
    
                    const data = await getUserInfo(userEmail);
                    setUser(data);
                    setUserId(data.data.id);
                    setUserName(data.data.firstName + " " + data.data.lastName);
                } catch (error) {
                    signOut();
                    setLoading(false);
                }
            }
            setLoading(false);
        };
    
        fetchUserInfo();
    }, []);

    const signIn = async (email: string, password: string) => {
        try {
            const data = await login({ email, password, grant_type: "password" });
            localStorage.setItem("token", data.accessToken);
            localStorage.setItem("role", data.role);

            const dataUser = await getUserInfo(email);
            setUserId(dataUser.data.id);
            setUserName(`${dataUser.data.firstName} ${dataUser.data.lastName}`);

            if (dataUser.data.groups.length > 0) {
                const userGroups: number[] = dataUser.data.groups.map((group: GroupOption) => group.id);
                localStorage.setItem("groupes", JSON.stringify(userGroups));
                setGroupes(userGroups);
            }

            setRole(data.role);
        } catch (error) {
            throw error;
        }
    };

    const signOut = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("role");
        localStorage.removeItem("groupes");
        setRole(null);
        setUser(null);
        setUserName(null);
        setGroupes(null);
    };

    const setAuthData = ({ role, groupes }: { role?: string | null; groupes?: number[] | null }) => {
        if (role !== undefined) {
            setRole(role);
            localStorage.setItem("role", role || "");
        }

        if (groupes !== undefined) {
            setGroupes(groupes);
            localStorage.setItem("groupes", JSON.stringify(groupes || []));
        }
    };

    return (
        <AuthContext.Provider value={{ user, userId, userName, role, groupes, signIn, signOut, setAuthData }}>
            {!loading ? children : <p>Chargement...</p>}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("error provider");
    }
    return context;
};
